// src/components/lesson/diagrams/Circle.tsx
//
// General circle figure for SAT "Circles" geometry: radius/diameter, labeled
// points on the circumference, chords, a central angle (with optional shaded
// sector), an inscribed angle, a highlighted arc, and a tangent line. Covers
// circumference/area, arc length, sector area, central vs inscribed angles,
// chords, and tangent questions — the figures the bank previously could not
// render (only `unit-circle`, which is trig-specific, existed).
//
// Angle convention: degrees, 0° = east (right), counter-clockwise positive
// (standard math). A point at angle θ is (cx + r·cosθ, cy − r·sinθ) — the −sin
// flips SVG's y-down axis so the picture reads like a math diagram.
//
// Arcs and sectors are drawn by SAMPLING points along the arc (polyline /
// polygon) rather than SVG arc paths, which avoids large-arc/sweep-flag bugs.
//
// Params (all optional):
//   radiusLabel?: string            // draw + label a radius (at `radiusAngle`, default 90°)
//   radiusAngle?: number
//   diameterLabel?: string          // draw + label a horizontal diameter
//   showCenter?: boolean            // draw the center dot (default true if any radius/angle)
//   centerLabel?: string            // default "O"
//   points?: { angleDeg, label? }[] // labeled points on the circle
//   chords?: { from, to, label? }[] // chord between two angles
//   centralAngle?: { from, to, label?, shade? }   // two radii + angle arc (+ shaded sector)
//   inscribedAngle?: { vertex, from, to, label? } // vertex on circle, two chords, angle label
//   arc?: { from, to, label? }      // highlighted arc (CCW from→to) + label
//   tangentAt?: number              // tangent line at this angle (+ right-angle marker)
//   withSquare?: "circumscribed" | "inscribed"  // draw a square with the circle:
//       "circumscribed" = square AROUND the circle, sides tangent to it (a circle
//         INSCRIBED in a square — side = diameter);
//       "inscribed" = square INSIDE the circle, corners on it (a square inscribed
//         in a circle — diagonal = diameter).
//   squareLabel?: string            // label for the square's side
//   label?: string                  // aria-label

type AnglePoint = { angleDeg: number; label?: string }
type Chord = { from: number; to: number; label?: string }
type CentralAngle = { from: number; to: number; label?: string; shade?: boolean }
type InscribedAngle = { vertex: number; from: number; to: number; label?: string }
type Arc = { from: number; to: number; label?: string }

type CircleParams = {
  radiusLabel?: string
  radiusAngle?: number
  diameterLabel?: string
  showCenter?: boolean
  centerLabel?: string
  points?: AnglePoint[]
  chords?: Chord[]
  centralAngle?: CentralAngle
  inscribedAngle?: InscribedAngle
  arc?: Arc
  tangentAt?: number
  withSquare?: 'circumscribed' | 'inscribed'
  squareLabel?: string
  label?: string
}

const SIZE = 320
const PAD = 46
const CX = SIZE / 2
const CY = SIZE / 2
const R = SIZE / 2 - PAD

const SHAPE = 'rgb(148 163 184)' // slate — circle outline
const ACCENT = 'rgb(34 211 238)' // cyan — radii, chords, points
const ANGLE = 'rgb(251 191 36)' // amber — angle labels + arcs
const HILITE = 'rgb(52 211 153)' // emerald — highlighted arc

const rad = (deg: number) => (deg * Math.PI) / 180
const pointAt = (deg: number, r = R) => ({
  x: CX + r * Math.cos(rad(deg)),
  y: CY - r * Math.sin(rad(deg)),
})

/** Sample points along the CCW arc from a1 to a2 (a2 bumped past a1 if needed). */
function arcSamples(a1: number, a2: number, r = R, steps = 40): { x: number; y: number }[] {
  let end = a2
  while (end < a1) end += 360
  const out: { x: number; y: number }[] = []
  for (let i = 0; i <= steps; i++) {
    out.push(pointAt(a1 + ((end - a1) * i) / steps, r))
  }
  return out
}

const ptsAttr = (pts: { x: number; y: number }[]) => pts.map((p) => `${p.x},${p.y}`).join(' ')

export default function Circle({ params }: { params: Record<string, unknown> }) {
  const p = params as CircleParams
  const num = (v: unknown, d: number) => (typeof v === 'number' && Number.isFinite(v) ? v : d)

  const points = Array.isArray(p.points) ? p.points : []
  const chords = Array.isArray(p.chords) ? p.chords : []
  const showCenter =
    p.showCenter !== false &&
    (p.showCenter === true ||
      p.radiusLabel !== undefined ||
      p.centralAngle !== undefined ||
      p.diameterLabel !== undefined)
  const centerLabel = typeof p.centerLabel === 'string' ? p.centerLabel : 'O'

  // Push a point's outside label slightly beyond the circumference.
  const labelPos = (deg: number) => pointAt(deg, R + 16)

  return (
    <svg
      viewBox={`0 0 ${SIZE} ${SIZE}`}
      role="img"
      aria-label={p.label ?? 'Circle figure'}
      className="my-3 w-full max-w-xs rounded-md bg-card/60 ring-1 ring-slate-800"
    >
      {/* Shaded sector for a central angle */}
      {p.centralAngle?.shade && (
        <polygon
          points={ptsAttr([
            { x: CX, y: CY },
            ...arcSamples(p.centralAngle.from, p.centralAngle.to),
          ])}
          fill="rgb(251 191 36 / 0.15)"
          stroke="none"
        />
      )}

      {/* Optional square drawn WITH the circle (inscribed-figure questions).
          "circumscribed" = square around the circle (circle inscribed in it);
          "inscribed" = square inside the circle (corners on it). Drawn before the
          circle so the circle outline sits crisply on top at the touch points. */}
      {(p.withSquare === 'circumscribed' || p.withSquare === 'inscribed') &&
        (() => {
          const corners =
            p.withSquare === 'circumscribed'
              ? [
                  { x: CX - R, y: CY - R },
                  { x: CX + R, y: CY - R },
                  { x: CX + R, y: CY + R },
                  { x: CX - R, y: CY + R },
                ]
              : [pointAt(45), pointAt(135), pointAt(225), pointAt(315)]
          const bottomY = Math.max(...corners.map((c) => c.y))
          const sq = typeof p.squareLabel === 'string' ? p.squareLabel : undefined
          return (
            <>
              <polygon
                points={ptsAttr(corners)}
                fill="none"
                stroke={SHAPE}
                strokeWidth="1.5"
                strokeLinejoin="round"
              />
              {sq && (
                <text
                  x={CX}
                  y={bottomY + 16}
                  fontSize="13"
                  fontWeight="600"
                  textAnchor="middle"
                  fill={SHAPE}
                >
                  {sq}
                </text>
              )}
            </>
          )
        })()}

      {/* Circle outline */}
      <circle
        cx={CX}
        cy={CY}
        r={R}
        fill="rgb(34 211 238 / 0.05)"
        stroke={SHAPE}
        strokeWidth="1.5"
      />

      {/* Highlighted arc */}
      {p.arc && (
        <>
          <polyline
            points={ptsAttr(arcSamples(p.arc.from, p.arc.to))}
            fill="none"
            stroke={HILITE}
            strokeWidth="3.5"
          />
          {p.arc.label &&
            (() => {
              const mid =
                (p.arc.to < p.arc.from ? p.arc.from + p.arc.to + 360 : p.arc.from + p.arc.to) / 2
              const lp = pointAt(mid, R + 18)
              return (
                <text
                  x={lp.x}
                  y={lp.y}
                  fontSize="12"
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fill={HILITE}
                >
                  {p.arc.label}
                </text>
              )
            })()}
        </>
      )}

      {/* Diameter */}
      {p.diameterLabel !== undefined &&
        (() => {
          const a = pointAt(180)
          const b = pointAt(0)
          return (
            <>
              <line x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke={ACCENT} strokeWidth="1.5" />
              <text x={CX} y={CY - 6} fontSize="12" textAnchor="middle" fill={ACCENT}>
                {p.diameterLabel}
              </text>
            </>
          )
        })()}

      {/* Radius */}
      {p.radiusLabel !== undefined &&
        (() => {
          const a = num(p.radiusAngle, 90)
          const end = pointAt(a)
          const mid = pointAt(a, R / 2)
          return (
            <>
              <line x1={CX} y1={CY} x2={end.x} y2={end.y} stroke={ACCENT} strokeWidth="1.5" />
              <text
                x={mid.x + 8}
                y={mid.y}
                fontSize="12"
                textAnchor="start"
                dominantBaseline="middle"
                fill={ACCENT}
              >
                {p.radiusLabel}
              </text>
            </>
          )
        })()}

      {/* Central angle: two radii + angle label near the center */}
      {p.centralAngle &&
        (() => {
          const f = pointAt(p.centralAngle.from)
          const t = pointAt(p.centralAngle.to)
          let mid =
            (p.centralAngle.to < p.centralAngle.from
              ? p.centralAngle.from + p.centralAngle.to + 360
              : p.centralAngle.from + p.centralAngle.to) / 2
          const lp = pointAt(mid, 34)
          return (
            <>
              <line x1={CX} y1={CY} x2={f.x} y2={f.y} stroke={ACCENT} strokeWidth="1.5" />
              <line x1={CX} y1={CY} x2={t.x} y2={t.y} stroke={ACCENT} strokeWidth="1.5" />
              <polyline
                points={ptsAttr(arcSamples(p.centralAngle.from, p.centralAngle.to, 22))}
                fill="none"
                stroke={ANGLE}
                strokeWidth="1.5"
              />
              {p.centralAngle.label && (
                <text
                  x={lp.x}
                  y={lp.y}
                  fontSize="12"
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fill={ANGLE}
                >
                  {p.centralAngle.label}
                </text>
              )}
            </>
          )
        })()}

      {/* Inscribed angle: vertex on the circle, two chords to from/to points */}
      {p.inscribedAngle &&
        (() => {
          const v = pointAt(p.inscribedAngle.vertex)
          const f = pointAt(p.inscribedAngle.from)
          const t = pointAt(p.inscribedAngle.to)
          const lp = pointAt(p.inscribedAngle.vertex, R - 26)
          return (
            <>
              <line x1={v.x} y1={v.y} x2={f.x} y2={f.y} stroke={ACCENT} strokeWidth="1.5" />
              <line x1={v.x} y1={v.y} x2={t.x} y2={t.y} stroke={ACCENT} strokeWidth="1.5" />
              {p.inscribedAngle.label && (
                <text
                  x={lp.x}
                  y={lp.y}
                  fontSize="12"
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fill={ANGLE}
                >
                  {p.inscribedAngle.label}
                </text>
              )}
            </>
          )
        })()}

      {/* Chords */}
      {chords.map((c, i) => {
        const f = pointAt(c.from)
        const t = pointAt(c.to)
        return (
          <g key={`chord-${i}`}>
            <line x1={f.x} y1={f.y} x2={t.x} y2={t.y} stroke={ACCENT} strokeWidth="1.5" />
            {c.label && (
              <text
                x={(f.x + t.x) / 2}
                y={(f.y + t.y) / 2 - 6}
                fontSize="12"
                textAnchor="middle"
                fill={ACCENT}
              >
                {c.label}
              </text>
            )}
          </g>
        )
      })}

      {/* Tangent line at a point (perpendicular to the radius there) */}
      {typeof p.tangentAt === 'number' &&
        (() => {
          const a = p.tangentAt
          const tp = pointAt(a)
          // Direction perpendicular to the radius = tangent direction.
          const tx = -Math.sin(rad(a))
          const ty = -Math.cos(rad(a))
          const L = 54
          return (
            <line
              x1={tp.x - tx * L}
              y1={tp.y - ty * L}
              x2={tp.x + tx * L}
              y2={tp.y + ty * L}
              stroke={ANGLE}
              strokeWidth="1.5"
            />
          )
        })()}

      {/* Center dot + label */}
      {showCenter && (
        <>
          <circle cx={CX} cy={CY} r="2.5" fill={SHAPE} />
          <text x={CX - 8} y={CY + 13} fontSize="12" textAnchor="middle" fill={SHAPE}>
            {centerLabel}
          </text>
        </>
      )}

      {/* Labeled points on the circumference */}
      {points.map((pt, i) => {
        const c = pointAt(pt.angleDeg)
        const lp = labelPos(pt.angleDeg)
        return (
          <g key={`pt-${i}`}>
            <circle cx={c.x} cy={c.y} r="3" fill={ACCENT} />
            {pt.label && (
              <text
                x={lp.x}
                y={lp.y}
                fontSize="12"
                fontWeight="600"
                textAnchor="middle"
                dominantBaseline="middle"
                fill={ACCENT}
              >
                {pt.label}
              </text>
            )}
          </g>
        )
      })}
    </svg>
  )
}
